import { useState, useEffect, useCallback, useRef } from 'react';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { reportTTSError, reportSTTError, trackTTSUsage, trackSTTUsage, trackVoiceAttempt } from '../utils/errorReporting';

/**
 * Voice fallback rules by language
 */
const VOICE_PREFERENCES = {
  es: {
    preferred: ['es-ES', 'es-US', 'es-MX'],
    fallbackPattern: /^es/i,
    messageKey: 'audio.spanishVoiceNotInstalled'
  },
  fr: {
    preferred: ['fr-FR', 'fr-CA'],
    fallbackPattern: /^fr/i,
    messageKey: 'audio.frenchVoiceNotInstalled'
  },
  ar: {
    preferred: ['ar-SA', 'ar-AE', 'ar-EG'],
    fallbackPattern: /^ar/i,
    messageKey: 'audio.arabicVoiceNotInstalled'
  },
  en: {
    preferred: ['en-US', 'en-GB'],
    fallbackPattern: /^en/i,
    messageKey: 'audio.englishVoiceNotInstalled'
  },
  zh: {
    preferred: ['zh-CN', 'zh-TW', 'cmn-Hans-CN'],
    fallbackPattern: /^(zh|cmn)/i,
    messageKey: 'audio.chineseVoiceNotInstalled'
  },
  ja: {
    preferred: ['ja-JP', 'ja'],
    fallbackPattern: /^ja/i,
    messageKey: 'audio.japaneseVoiceNotInstalled'
  },
  ko: {
    preferred: ['ko-KR', 'ko'],
    fallbackPattern: /^ko/i,
    messageKey: 'audio.koreanVoiceNotInstalled'
  },
  vi: {
    preferred: ['vi-VN', 'vi'],
    fallbackPattern: /^vi/i,
    messageKey: 'audio.vietnameseVoiceNotInstalled'
  },
  th: {
    preferred: ['th-TH', 'th'],
    fallbackPattern: /^th/i,
    messageKey: 'audio.thaiVoiceNotInstalled'
  }
};

/**
 * Custom hook for managing TTS (Text-to-Speech) functionality
 * with proper voice loading, fallbacks, and error handling
 */
export const useAudioManager = () => {
  const { t } = useTranslation();
  const [ttsAvailable, setTtsAvailable] = useState(true);
  const [ttsError, setTtsError] = useState(null);
  const [voicesLoaded, setVoicesLoaded] = useState(false);
  const [availableVoices, setAvailableVoices] = useState({});
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [selectedVoiceInfo, setSelectedVoiceInfo] = useState(null);
  
  const voicesRef = useRef([]);
  const utteranceRef = useRef(null);

  /**
   * Find the best voice for a given language
   */
  const findBestVoice = useCallback((langCode) => {
    const voices = voicesRef.current;
    const prefs = VOICE_PREFERENCES[langCode];
    
    if (!prefs) {
      // Unknown language, try to find any matching voice
      const anyVoice = voices.find(v => v.lang.toLowerCase().startsWith(langCode.toLowerCase()));
      return anyVoice ? { voice: anyVoice, fallback: false } : null;
    }

    // Try preferred voices first (exact match)
    for (const preferredLang of prefs.preferred) {
      const exactMatch = voices.find(v => 
        v.lang.toLowerCase() === preferredLang.toLowerCase()
      );
      if (exactMatch) {
        return { voice: exactMatch, fallback: false };
      }
    }

    // Try any voice matching the pattern
    const patternMatch = voices.find(v => prefs.fallbackPattern.test(v.lang));
    if (patternMatch) {
      return { voice: patternMatch, fallback: true };
    }

    return null;
  }, []);

  /**
   * Load and categorize available voices
   */
  const loadVoices = useCallback(() => {
    if (!('speechSynthesis' in window)) {
      setTtsAvailable(false);
      setTtsError(t('audio.ttsNotSupported'));
      return;
    }

    const voices = window.speechSynthesis.getVoices();
    voicesRef.current = voices;

    if (voices.length === 0) {
      return false; // Voices not loaded yet
    }

    // Categorize available voices by language
    const voicesByLang = {};
    for (const langCode of Object.keys(VOICE_PREFERENCES)) {
      const result = findBestVoice(langCode);
      const prefs = VOICE_PREFERENCES[langCode];
      voicesByLang[langCode] = {
        available: !!result,
        voice: result?.voice || null,
        isFallback: result?.fallback || false,
        voiceName: result?.voice?.name || null,
        voiceLang: result?.voice?.lang || null,
        unavailableMessage: result ? null : t(prefs?.messageKey || 'audio.voiceNotAvailable')
      };
    }

    setAvailableVoices(voicesByLang);
    setVoicesLoaded(true);
    return true;
  }, [findBestVoice, t]);

  /**
   * Initialize TTS and listen for voice changes
   */
  useEffect(() => {
    if (!('speechSynthesis' in window)) {
      setTtsAvailable(false);
      setTtsError(t('audio.ttsNotSupported'));
      return;
    }

    // Try to load voices immediately
    if (!loadVoices()) {
      // Voices not ready, listen for the event
      const handleVoicesChanged = () => {
        loadVoices();
      };

      window.speechSynthesis.onvoiceschanged = handleVoicesChanged;

      // Fallback: retry after a delay (some browsers need this)
      const retryTimeout = setTimeout(() => {
        if (!loadVoices()) {
          // Still no voices after retry - set as loaded but empty
          setVoicesLoaded(true);
          setTtsError(t('audio.noVoicesAvailable'));
        }
      }, 1000);

      // Final timeout - give up after 3 seconds
      const finalTimeout = setTimeout(() => {
        if (!voicesLoaded) {
          setVoicesLoaded(true);
          // Check one more time
          const voices = window.speechSynthesis.getVoices();
          if (voices.length === 0) {
            setTtsError(t('audio.noVoicesAvailable'));
          }
        }
      }, 3000);

      return () => {
        window.speechSynthesis.onvoiceschanged = null;
        clearTimeout(retryTimeout);
        clearTimeout(finalTimeout);
      };
    }
  }, [loadVoices, voicesLoaded, t]);

  /**
   * Check if TTS is available for a specific language
   */
  const isLanguageSupported = useCallback((langCode) => {
    if (!voicesLoaded) return null; // Unknown yet
    return availableVoices[langCode]?.available || false;
  }, [voicesLoaded, availableVoices]);

  /**
   * Get voice info for a language
   */
  const getVoiceInfo = useCallback((langCode) => {
    return availableVoices[langCode] || null;
  }, [availableVoices]);

  /**
   * Speak text in the specified language
   * IMPORTANT: This must be called directly from a user gesture (click handler)
   * to work properly on all browsers
   */
  const speak = useCallback((text, langCode, onComplete) => {
    if (!text) {
      toast.error(t('common.nothingToPlay'));
      return false;
    }

    if (!('speechSynthesis' in window)) {
      toast.error(t('audio.audioNotSupported'));
      return false;
    }

    // Cancel any ongoing speech
    window.speechSynthesis.cancel();

    const voiceInfo = availableVoices[langCode];
    
    if (!voiceInfo?.available) {
      const prefs = VOICE_PREFERENCES[langCode];
      const message = voiceInfo?.unavailableMessage || 
        t(prefs?.messageKey || 'audio.voiceNotAvailable');
      toast.error(message);
      return false;
    }

    try {
      const utterance = new SpeechSynthesisUtterance(text);
      utteranceRef.current = utterance;
      
      // Set the voice
      utterance.voice = voiceInfo.voice;
      utterance.lang = voiceInfo.voice.lang;
      
      // Adjust rate and pitch for better clarity
      utterance.rate = 0.9;
      utterance.pitch = 1;

      // Set up event handlers
      utterance.onstart = () => {
        setIsSpeaking(true);
        setSelectedVoiceInfo({
          name: voiceInfo.voice.name,
          lang: voiceInfo.voice.lang,
          isFallback: voiceInfo.isFallback
        });
      };

      utterance.onend = () => {
        setIsSpeaking(false);
        setSelectedVoiceInfo(null);
        onComplete?.();
      };

      utterance.onerror = (event) => {
        console.error('Speech synthesis error:', event);
        setIsSpeaking(false);
        setSelectedVoiceInfo(null);
        
        // Report TTS error for admin visibility
        reportTTSError(`TTS error: ${event.error}`, 'useAudioManager');
        
        if (event.error === 'not-allowed') {
          toast.error(t('audio.audioBlocked'));
        } else if (event.error === 'interrupted') {
          // User probably clicked something else, ignore
        } else if (event.error === 'canceled') {
          // Speech was canceled, ignore
        } else {
          toast.error(t('audio.audioPlaybackFailed'));
        }
      };

      // CRITICAL: speak() must be called synchronously from click handler
      window.speechSynthesis.speak(utterance);
      
      // Track TTS usage for admin metrics
      trackTTSUsage();
      
      return true;

    } catch (error) {
      console.error('TTS error:', error);
      toast.error(t('audio.audioPlaybackFailed'));
      return false;
    }
  }, [availableVoices, t]);

  /**
   * Stop any ongoing speech
   */
  const stopSpeaking = useCallback(() => {
    if ('speechSynthesis' in window) {
      window.speechSynthesis.cancel();
    }
    setIsSpeaking(false);
    setSelectedVoiceInfo(null);
  }, []);

  /**
   * Run a self-test for audio playback
   */
  const runSelfTest = useCallback((langCode) => {
    const testPhrases = {
      es: '¡Hola! El audio está funcionando correctamente.',
      fr: 'Bonjour! L\'audio fonctionne correctement.',
      ar: 'مرحبا! الصوت يعمل بشكل صحيح.',
      en: 'Hello! Audio is working correctly.'
    };

    const phrase = testPhrases[langCode] || testPhrases.en;
    const success = speak(phrase, langCode, () => {
      toast.success(t('audio.audioTestComplete'));
    });

    if (success) {
      const voiceInfo = availableVoices[langCode];
      toast.info(
        `${t('audio.playingWith', { voiceName: voiceInfo?.voice?.name || 'Default' })}${voiceInfo?.isFallback ? ` ${t('audio.fallback')}` : ''}`
      );
    }

    return success;
  }, [speak, availableVoices, t]);

  return {
    // State
    ttsAvailable,
    ttsError,
    voicesLoaded,
    isSpeaking,
    selectedVoiceInfo,
    availableVoices,
    
    // Methods
    speak,
    stopSpeaking,
    isLanguageSupported,
    getVoiceInfo,
    runSelfTest
  };
};

/**
 * Custom hook for managing STT (Speech-to-Text) / microphone input
 * with proper multilingual support and iOS compatibility
 */
export const useMicrophoneManager = () => {
  const { t } = useTranslation();
  const [sttAvailable, setSttAvailable] = useState(true);
  const [sttError, setSttError] = useState(null);
  const [micPermission, setMicPermission] = useState('unknown'); // 'unknown', 'granted', 'denied', 'prompt'
  const [isListening, setIsListening] = useState(false);
  const [currentLang, setCurrentLang] = useState(null);
  const [isIOS, setIsIOS] = useState(false);
  const [isSafari, setIsSafari] = useState(false);
  const [platformInfo, setPlatformInfo] = useState(null);
  
  const recognitionRef = useRef(null);
  const isStartingRef = useRef(false); // Prevent double-starts

  /**
   * Language mapping for Speech Recognition
   * Each language has primary and fallback locales
   */
  const STT_LANG_MAP = {
    en: { primary: 'en-US', fallbacks: ['en-GB', 'en-AU', 'en'] },
    es: { primary: 'es-ES', fallbacks: ['es-MX', 'es-US', 'es-AR', 'es'] },
    fr: { primary: 'fr-FR', fallbacks: ['fr-CA', 'fr'] },
    ar: { primary: 'ar-SA', fallbacks: ['ar-AE', 'ar-EG', 'ar'] }
  };

  /**
   * Detect platform and check STT availability
   */
  useEffect(() => {
    // Platform detection
    const userAgent = navigator.userAgent || navigator.vendor || window.opera;
    const platform = navigator.platform || '';
    
    // iOS detection (iPhone, iPad, iPod)
    const iosDetected = /iPad|iPhone|iPod/.test(userAgent) || 
      (platform === 'MacIntel' && navigator.maxTouchPoints > 1); // iPad Pro
    
    // Safari detection
    const safariDetected = /^((?!chrome|android).)*safari/i.test(userAgent);
    
    // Browser info for logging
    const browserInfo = {
      userAgent: userAgent.substring(0, 100),
      platform,
      isIOS: iosDetected,
      isSafari: safariDetected,
      isIOSSafari: iosDetected && safariDetected,
      maxTouchPoints: navigator.maxTouchPoints
    };
    
    setIsIOS(iosDetected);
    setIsSafari(safariDetected);
    setPlatformInfo(browserInfo);
    
    console.log('[STT] Platform detection:', browserInfo);
    
    // Check if Web Speech API is available
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    
    if (!SpeechRecognition) {
      setSttAvailable(false);
      // iOS-specific message
      if (iosDetected) {
        setSttError(t('mic.iosNotSupported'));
        console.warn('[STT] Web Speech API not supported on iOS');
      } else {
        setSttError(t('mic.sttNotSupported'));
        console.warn('[STT] Web Speech API not supported in this browser');
      }
      return;
    }

    console.log('[STT] Web Speech API available');
    
    // iOS Safari specific warnings
    if (iosDetected && safariDetected) {
      console.log('[STT] iOS Safari detected - STT may have restrictions');
      console.log('[STT] Note: recognition.start() must be called from user gesture');
    }

    // Check microphone permission if available
    // Note: iOS Safari doesn't support permissions.query for microphone
    if (navigator.permissions && navigator.permissions.query) {
      navigator.permissions.query({ name: 'microphone' })
        .then((result) => {
          setMicPermission(result.state);
          console.log('[STT] Microphone permission:', result.state);
          
          result.onchange = () => {
            setMicPermission(result.state);
            console.log('[STT] Microphone permission changed:', result.state);
          };
        })
        .catch((err) => {
          // Permission API not supported for microphone (common on iOS)
          setMicPermission('unknown');
          console.log('[STT] Permission API not available for microphone:', err.message);
        });
    } else {
      console.log('[STT] Permissions API not available (likely iOS)');
      setMicPermission('unknown');
    }
  }, [t]);

  /**
   * Get the best STT language code for recognition
   */
  const getSTTLanguageCode = useCallback((langCode) => {
    const langConfig = STT_LANG_MAP[langCode];
    if (langConfig) {
      console.log(`[STT] Language ${langCode} -> Using ${langConfig.primary}`);
      return langConfig.primary;
    }
    // Fallback for unknown languages
    console.warn(`[STT] Unknown language code: ${langCode}, defaulting to en-US`);
    return 'en-US';
  }, []);

  /**
   * Check if STT is supported for a specific language
   */
  const isSTTLanguageSupported = useCallback((langCode) => {
    return !!STT_LANG_MAP[langCode];
  }, []);

  /**
   * Start listening for voice input
   * CRITICAL: This must be called directly from a user gesture (click handler)
   * Do not call this from async functions, useEffect, or setTimeout
   */
  const startListening = useCallback((langCode, onResult, onError) => {
    // Prevent double-starts
    if (isStartingRef.current) {
      console.log('[STT] Already starting, ignoring duplicate call');
      return false;
    }
    
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    
    if (!SpeechRecognition) {
      console.error('[STT] SpeechRecognition not available');
      if (isIOS) {
        toast.error(t('mic.iosNotSupported'));
      } else {
        toast.error(t('mic.sttNotSupported'));
      }
      onError?.('not-supported');
      return false;
    }

    // Stop any existing recognition
    if (recognitionRef.current) {
      console.log('[STT] Stopping existing recognition');
      try {
        recognitionRef.current.abort();
      } catch (e) {
        console.warn('[STT] Error aborting previous recognition:', e);
      }
      recognitionRef.current = null;
    }

    isStartingRef.current = true;
    console.log('[STT] Starting recognition...', { langCode, isIOS, isSafari });

    try {
      const recognition = new SpeechRecognition();
      recognitionRef.current = recognition;

      // Get the correct language code for STT
      const sttLang = getSTTLanguageCode(langCode);
      
      // Configure recognition - CRITICAL: Set language BEFORE starting
      recognition.lang = sttLang;
      recognition.continuous = false;
      recognition.interimResults = !isIOS; // iOS has issues with interim results
      recognition.maxAlternatives = 1;

      // Log configuration for debugging
      console.log('[STT] Recognition configured:', {
        requestedLang: langCode,
        recognitionLang: recognition.lang,
        continuous: recognition.continuous,
        interimResults: recognition.interimResults,
        isIOS,
        isSafari
      });

      setCurrentLang(langCode);

      recognition.onstart = () => {
        isStartingRef.current = false;
        setIsListening(true);
        console.log(`[STT] Started listening in ${recognition.lang}`);
      };

      recognition.onend = () => {
        isStartingRef.current = false;
        setIsListening(false);
        setCurrentLang(null);
        recognitionRef.current = null;
        console.log('[STT] Stopped listening');
      };

      recognition.onresult = (event) => {
        const results = event.results;
        const latestResult = results[results.length - 1];
        const transcript = latestResult[0].transcript;
        const confidence = latestResult[0].confidence;
        const isFinal = latestResult.isFinal;

        console.log('[STT] Result:', {
          transcript,
          confidence: confidence?.toFixed(2),
          isFinal,
          lang: recognition.lang
        });

        // For iOS, treat all results as final since interim is disabled
        // For other browsers, only call onResult for final results
        if (isFinal || isIOS) {
          // Track successful STT usage
          trackSTTUsage();
          onResult?.(transcript, confidence);
        }
      };

      recognition.onspeechstart = () => {
        console.log('[STT] Speech detected');
      };

      recognition.onspeechend = () => {
        console.log('[STT] Speech ended');
      };

      recognition.onaudiostart = () => {
        console.log('[STT] Audio capture started');
      };

      recognition.onaudioend = () => {
        console.log('[STT] Audio capture ended');
      };

      recognition.onnomatch = () => {
        console.warn('[STT] No match - speech not recognized');
        toast.info(t('mic.noSpeech'));
      };

      recognition.onerror = (event) => {
        // Extended logging for debugging - log full event object
        const errorDetails = {
          error: event.error,
          message: event.message || 'No message',
          type: event.type,
          timeStamp: event.timeStamp,
          isTrusted: event.isTrusted,
          // Recognition state
          recognitionLang: recognition.lang,
          recognitionContinuous: recognition.continuous,
          recognitionInterimResults: recognition.interimResults,
          // Platform info
          isIOS,
          isSafari,
          isIOSSafari: isIOS && isSafari,
          platform: platformInfo?.platform,
          userAgent: platformInfo?.userAgent?.substring(0, 80)
        };
        
        console.error('[STT] ❌ ERROR EVENT:', event.error);
        console.error('[STT] Full error details:', JSON.stringify(errorDetails, null, 2));
        console.table(errorDetails);
        
        // Report STT error for admin visibility (except aborted which is normal)
        if (event.error !== 'aborted' && event.error !== 'no-speech') {
          reportSTTError(
            `STT error: ${event.error} | Platform: ${isIOS ? 'ios' : (isSafari ? 'safari' : 'other')} | Lang: ${recognition.lang}`,
            'useAudioManager'
          );
        }
        
        // Immediately exit listening state - CRITICAL for iOS
        isStartingRef.current = false;
        setIsListening(false);
        setCurrentLang(null);
        recognitionRef.current = null;
        
        console.log('[STT] Listening state cleared after error');

        switch (event.error) {
          case 'not-allowed':
            console.error('[STT] FAILURE MODE: Permission denied (not-allowed)');
            setMicPermission('denied');
            // iOS-specific permission instructions
            if (isIOS) {
              toast.error(t('mic.iosMicDenied'), { duration: 6000 });
            } else {
              toast.error(t('mic.micDeniedToast'));
            }
            onError?.('permission-denied');
            break;
            
          case 'service-not-allowed':
            // iOS Safari specific error - most common on iOS
            console.error('[STT] FAILURE MODE: Service not allowed (iOS restriction)');
            console.error('[STT] This typically means iOS Safari has blocked STT');
            if (isIOS) {
              toast.error(t('mic.iosServiceNotAllowed'), { duration: 5000 });
            } else {
              toast.error(t('mic.serviceNotAllowed'));
            }
            // Suggest typing instead
            toast.info(t('mic.useTypingInstead'), { duration: 4000 });
            onError?.('service-not-allowed');
            break;
            
          case 'no-speech':
            console.log('[STT] FAILURE MODE: No speech detected');
            toast.info(t('mic.noSpeech'));
            onError?.('no-speech');
            break;
            
          case 'audio-capture':
            console.error('[STT] FAILURE MODE: Audio capture failed');
            if (isIOS) {
              toast.error(t('mic.iosAudioCapture'), { duration: 5000 });
            } else {
              toast.error(t('mic.noMicrophone'));
            }
            onError?.('no-microphone');
            break;
            
          case 'network':
            console.error('[STT] FAILURE MODE: Network error');
            toast.error(t('mic.networkError'));
            onError?.('network');
            break;
            
          case 'language-not-supported':
            console.error(`[STT] FAILURE MODE: Language not supported - ${recognition.lang}`);
            toast.error(t('mic.languageNotSupported', { lang: langCode.toUpperCase() }));
            onError?.('language-not-supported');
            break;
            
          case 'aborted':
            // User or code cancelled, ignore
            console.log('[STT] Recognition aborted (user/code initiated)');
            break;
            
          default:
            console.error(`[STT] FAILURE MODE: Unknown error - ${event.error}`);
            toast.error(`${t('mic.voiceInputError')}: ${event.error}`);
            onError?.(event.error);
        }
        
        console.log('[STT] Error handling complete - UI should be reset');
      };

      // CRITICAL: Start recognition immediately (must be in user gesture context)
      // Do NOT add any async operations before this line
      recognition.start();
      console.log(`[STT] Recognition.start() called for language: ${sttLang}`);
      return true;

    } catch (error) {
      console.error('[STT] Failed to start:', error);
      isStartingRef.current = false;
      setIsListening(false);
      setCurrentLang(null);
      
      // iOS-specific error handling
      if (isIOS && error.name === 'InvalidStateError') {
        toast.error(t('mic.iosInvalidState'));
      } else {
        toast.error(t('mic.failedToStart'));
      }
      
      // Suggest typing as fallback
      toast.info(t('mic.useTypingInstead'), { duration: 3000 });
      onError?.('start-failed');
      return false;
    }
  }, [t, getSTTLanguageCode, isIOS, isSafari, platformInfo]);

  /**
   * Stop listening and clean up
   */
  const stopListening = useCallback(() => {
    isStartingRef.current = false;
    if (recognitionRef.current) {
      console.log('[STT] Stopping recognition');
      try {
        recognitionRef.current.abort();
      } catch (e) {
        console.warn('[STT] Error stopping recognition:', e);
      }
      recognitionRef.current = null;
    }
    setIsListening(false);
    setCurrentLang(null);
  }, []);

  /**
   * Get microphone permission status message (with iOS-specific messages)
   */
  const getMicStatusMessage = useCallback(() => {
    if (!sttAvailable) {
      return sttError;
    }
    
    // iOS-specific message when permission is unknown
    if (isIOS && micPermission === 'unknown') {
      return t('mic.iosTapToAllow');
    }
    
    switch (micPermission) {
      case 'denied':
        return isIOS ? t('mic.iosMicDeniedDesc') : t('mic.micDeniedDesc');
      case 'prompt':
        return t('common.speak');
      default:
        return null;
    }
  }, [sttAvailable, sttError, micPermission, isIOS, t]);

  /**
   * Get iOS permission instructions
   */
  const getIOSPermissionInstructions = useCallback(() => {
    if (!isIOS) return null;
    return {
      settings: t('mic.iosSettingsInstructions'),
      safari: t('mic.iosSafariInstructions')
    };
  }, [isIOS, t]);

  return {
    // State
    sttAvailable,
    sttError,
    micPermission,
    isListening,
    currentLang,
    isIOS,
    isSafari,
    platformInfo,
    
    // Methods
    startListening,
    stopListening,
    getMicStatusMessage,
    isSTTLanguageSupported,
    getIOSPermissionInstructions,
    
    // Constants (for debugging)
    STT_LANG_MAP
  };
};

export default useAudioManager;
